import {
  type ValuationMethod,
  type MethodRequirement,
  METHOD_REQUIREMENTS,
} from "./method-requirements";

import {
  type ReportType,
  type DataDependency,
  type ReportSectionRequirement,
  REPORT_REQUIREMENTS,
} from "./report-requirements";

// ── Data State (what we have) ─────────────────────────────────────────────────

export interface DataState {
  metrics: { metric: string; years: number[] }[];
  documents: { type: string; fileName?: string }[];
  fields: { key: string; present: boolean }[];
  methodResults: { method: string; present: boolean }[];
  research: { category: string; present: boolean }[];
  unresolvedConflicts: { metric: string; year: number }[];
}

// ── Output Types ──────────────────────────────────────────────────────────────

export interface MethodReadinessResult {
  method: ValuationMethod;
  label: string;
  ready: boolean;
  progress: number;
  blockers: string[];
}

export interface SectionReadiness {
  section: string;
  label: string;
  ready: boolean;
  importance: "critical" | "recommended" | "optional";
  missingDependencies: string[];
}

export interface MissingDataItem {
  description: string;
  importance: "critical" | "recommended";
  source: "user" | "system";
  unlocks: string[];
  suggestedDocTypes: string[];
  suggestedAction: string;
}

export interface ReportReadinessResult {
  reportType: ReportType;
  label: string;
  ready: boolean;
  progress: number;
  sections: SectionReadiness[];
  missingData: MissingDataItem[];
  userActions: MissingDataItem[];
  pendingSystem: MissingDataItem[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getMetricYears(state: DataState, metric: string): number[] {
  return state.metrics.find((m) => m.metric === metric)?.years ?? [];
}

function hasField(state: DataState, key: string): boolean {
  return state.fields.find((f) => f.key === key)?.present ?? false;
}

function hasMethodResult(state: DataState, method: string): boolean {
  if (method === "any") {
    return state.methodResults.some((m) => m.present);
  }
  return state.methodResults.find((m) => m.method === method)?.present ?? false;
}

function hasResearch(state: DataState, category: string): boolean {
  return state.research.find((r) => r.category === category)?.present ?? false;
}

function hasDocument(state: DataState, docType: string): boolean {
  return state.documents.some((d) => d.type === docType);
}

function hasConflictsForMetric(state: DataState, metric: string): boolean {
  return state.unresolvedConflicts.some((c) => c.metric === metric);
}

// ── Method Readiness Evaluator ────────────────────────────────────────────────

export function evaluateMethodReadiness(
  method: ValuationMethod,
  state: DataState,
): MethodReadinessResult {
  const req = METHOD_REQUIREMENTS[method];
  const blockers: string[] = [];
  let totalChecks = 0;
  let passedChecks = 0;

  for (const field of req.requiredFields) {
    if (field.importance === "required") {
      totalChecks++;
      if (hasField(state, field.field)) {
        passedChecks++;
      } else {
        blockers.push(field.description);
      }
    }
  }

  for (const metric of req.requiredMetrics) {
    if (metric.importance === "required") {
      totalChecks++;
      const years = getMetricYears(state, metric.metric);
      const hasEnoughYears = years.length >= metric.minimumYears;
      const hasConflict = hasConflictsForMetric(state, metric.metric);

      if (hasEnoughYears && !hasConflict) {
        passedChecks++;
      } else if (hasConflict) {
        blockers.push(`Resolve data conflict for ${metric.description}`);
      } else {
        blockers.push(`Upload ${metric.description.toLowerCase()}`);
      }
    }
  }

  if (req.minimumYears > 1) {
    totalChecks++;
    const allYears = new Set(state.metrics.flatMap((m) => m.years));
    if (allYears.size >= req.minimumYears) {
      passedChecks++;
    } else {
      const existing = allYears.size;
      if (!blockers.some((b) => b.includes("year"))) {
        blockers.push(
          `Upload data for at least ${req.minimumYears} fiscal years (currently have ${existing})`,
        );
      }
    }
  }

  const progress = totalChecks === 0 ? 100 : Math.round((passedChecks / totalChecks) * 100);

  return {
    method,
    label: req.label,
    ready: blockers.length === 0,
    progress,
    blockers,
  };
}

export function evaluateAllMethodReadiness(
  state: DataState,
): Record<ValuationMethod, MethodReadinessResult> {
  return {
    MULTIPLES: evaluateMethodReadiness("MULTIPLES", state),
    DCF: evaluateMethodReadiness("DCF", state),
    ASSET_BASED: evaluateMethodReadiness("ASSET_BASED", state),
    RULE_OF_THUMB: evaluateMethodReadiness("RULE_OF_THUMB", state),
  };
}

// ── Report Readiness Evaluator ────────────────────────────────────────────────

function isDependencySatisfied(dep: DataDependency, state: DataState): boolean {
  switch (dep.type) {
    case "metric": {
      const years = getMetricYears(state, dep.key);
      const minYears = dep.minimumYears ?? 1;
      return years.length >= minYears && !hasConflictsForMetric(state, dep.key);
    }
    case "method_result":
      return hasMethodResult(state, dep.key);
    case "research":
      return hasResearch(state, dep.key);
    case "field": {
      if (dep.key === "askingPrice_or_valuation") {
        return hasField(state, "askingPrice") || hasMethodResult(state, "any");
      }
      return hasField(state, dep.key);
    }
    case "document":
      return hasDocument(state, dep.key);
    default:
      return false;
  }
}

function evaluateSection(
  section: ReportSectionRequirement,
  state: DataState,
): SectionReadiness {
  const missingDependencies: string[] = [];

  for (const dep of section.dataDependencies) {
    if (!isDependencySatisfied(dep, state)) {
      missingDependencies.push(dep.description);
    }
  }

  return {
    section: section.section,
    label: section.label,
    ready: missingDependencies.length === 0,
    importance: section.importance,
    missingDependencies,
  };
}

/**
 * Determines if a section is blocked from a user-action perspective.
 * A section is "user-ready" if all its `source: "user"` dependencies are met.
 */
function isSectionUserReady(
  section: ReportSectionRequirement,
  state: DataState,
): boolean {
  for (const dep of section.dataDependencies) {
    if (dep.source === "user" && !isDependencySatisfied(dep, state)) {
      return false;
    }
  }
  return true;
}

function buildMissingDataItems(
  sections: SectionReadiness[],
  reportReq: typeof REPORT_REQUIREMENTS[ReportType],
): MissingDataItem[] {
  const itemMap = new Map<string, MissingDataItem>();

  for (const sectionReadiness of sections) {
    if (sectionReadiness.ready) continue;

    const sectionReq = reportReq.sections.find(
      (s) => s.section === sectionReadiness.section,
    );
    if (!sectionReq) continue;

    for (const dep of sectionReq.dataDependencies) {
      const key = `${dep.type}:${dep.key}:${dep.minimumYears ?? 1}`;
      const existing = itemMap.get(key);

      if (existing) {
        if (!existing.unlocks.includes(sectionReadiness.label)) {
          existing.unlocks.push(sectionReadiness.label);
        }
        if (
          sectionReadiness.importance === "critical" &&
          existing.importance !== "critical"
        ) {
          existing.importance = "critical";
        }
      } else {
        const importance: "critical" | "recommended" =
          sectionReadiness.importance === "critical" ? "critical" : "recommended";
        itemMap.set(key, {
          description: dep.description,
          importance,
          source: dep.source,
          unlocks: [sectionReadiness.label],
          suggestedDocTypes: dep.satisfiedByDocTypes ?? [],
          suggestedAction: buildSuggestedAction(dep),
        });
      }
    }
  }

  return Array.from(itemMap.values()).sort((a, b) => {
    if (a.importance === "critical" && b.importance !== "critical") return -1;
    if (b.importance === "critical" && a.importance !== "critical") return 1;
    return b.unlocks.length - a.unlocks.length;
  });
}

function buildSuggestedAction(dep: DataDependency): string {
  switch (dep.type) {
    case "metric":
      return `Upload a document containing ${dep.description.toLowerCase()}`;
    case "method_result":
      return "Will be generated when the valuation runs";
    case "research":
      return "Will be gathered automatically during valuation";
    case "field":
      return `Set the ${dep.description.toLowerCase()} in business details`;
    case "document":
      return `Upload a ${dep.description.toLowerCase()}`;
    default:
      return dep.description;
  }
}

export function evaluateReportReadiness(
  reportType: ReportType,
  state: DataState,
): ReportReadinessResult {
  const reportReq = REPORT_REQUIREMENTS[reportType];
  const sections = reportReq.sections.map((s) => evaluateSection(s, state));

  // Readiness is determined only by user-actionable dependencies on critical sections
  const criticalSectionReqs = reportReq.sections.filter((s) => s.importance === "critical");
  const allCriticalUserReady = criticalSectionReqs.every((s) => isSectionUserReady(s, state));

  // Progress only considers user-source dependencies
  const userDeps: { satisfied: boolean; weight: number }[] = [];
  for (const sectionReq of reportReq.sections) {
    const weight = sectionReq.importance === "critical" ? 3 : sectionReq.importance === "recommended" ? 2 : 1;
    for (const dep of sectionReq.dataDependencies) {
      if (dep.source === "user") {
        userDeps.push({ satisfied: isDependencySatisfied(dep, state), weight });
      }
    }
  }

  const totalWeight = userDeps.reduce((acc, d) => acc + d.weight, 0);
  const satisfiedWeight = userDeps.filter((d) => d.satisfied).reduce((acc, d) => acc + d.weight, 0);
  const progress = totalWeight === 0 ? 100 : Math.round((satisfiedWeight / totalWeight) * 100);

  const missingData = buildMissingDataItems(sections, reportReq);
  const userActions = missingData.filter((item) => item.source === "user");
  const pendingSystem = missingData.filter((item) => item.source === "system");

  return {
    reportType,
    label: reportReq.label,
    ready: allCriticalUserReady,
    progress,
    sections,
    missingData,
    userActions,
    pendingSystem,
  };
}

export function evaluateAllReportReadiness(
  state: DataState,
): Record<ReportType, ReportReadinessResult> {
  return {
    APPRAISAL: evaluateReportReadiness("APPRAISAL", state),
    CIM: evaluateReportReadiness("CIM", state),
  };
}
